import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiTrendingUp, FiUsers, FiHeart, FiArrowRight, FiStar } = FiIcons;

const Hero = () => {
  const stats = [
    { number: '500+', label: 'Creators Empowered', icon: FiUsers },
    { number: '2M+', label: 'Views Generated', icon: FiTrendingUp },
    { number: '50+', label: 'LGBTQ+ Brands Served', icon: FiHeart },
    { number: '98%', label: 'Client Satisfaction', icon: FiStar }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Video Background Layer */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Enhanced Dark Overlay for Readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/80 z-10" />
        <div className="absolute inset-0 bg-black/40 z-10 backdrop-blur-[1px]" />
        
        {/* Direct MP4 Video Tag */}
        <video
          autoPlay
          muted
          loop
          playsInline
          disablePictureInPicture
          className="absolute top-1/2 left-1/2 w-full h-full min-w-full min-h-full -translate-x-1/2 -translate-y-1/2 object-cover opacity-80"
          poster="https://s3.us-east-1.wasabisys.com/createspace/CSM-Logo.png"
        >
          <source 
            src="https://video.gumlet.io/64b148d547459a13c34211cf/69821294924a60df4b75e84f/download.mp4" 
            type="video/mp4" 
          />
          Your browser does not support the video tag.
        </video>
      </div>

      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <div className="text-center">
          {/* Badge */}
          <motion.div
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-500/30 to-pink-500/30 backdrop-blur-md border border-purple-500/40 rounded-full px-6 py-2 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <SafeIcon icon={FiHeart} className="text-pink-400 text-sm" />
            <span className="text-sm font-black tracking-widest text-white uppercase">NYC's Premier LGBTQ+ Media Agency</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-8xl font-black mb-8 leading-tight tracking-tight drop-shadow-[0_10px_10px_rgba(0,0,0,0.5)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="bg-gradient-to-r from-white via-purple-100 to-pink-100 bg-clip-text text-transparent">
              Empowering
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              LGBTQ+ Creators
            </span>
            <br />
            <span className="text-white">
              to Own Their Space
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-xl lg:text-2xl text-gray-100 mb-12 max-w-4xl mx-auto leading-relaxed font-bold drop-shadow-[0_5px_5px_rgba(0,0,0,0.5)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Create Space Media is New York City's leading media agency specializing in
            <span className="text-purple-400 font-black"> OTT channel development</span>,
            <span className="text-pink-400 font-black"> YouTube management</span>, and
            <span className="text-purple-400 font-black"> community-driven platforms</span>.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <motion.button
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 px-12 py-5 rounded-full font-black text-xl shadow-[0_20px_50px_rgba(147,51,234,0.3)] hover:shadow-purple-500/50 transition-all duration-300 flex items-center space-x-3 min-w-[280px] justify-center text-white"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Launch Your Channel</span>
              <SafeIcon icon={FiPlay} />
            </motion.button>
            <motion.button
              className="bg-white/10 backdrop-blur-md border-2 border-white/30 hover:border-purple-400 text-white px-12 py-5 rounded-full font-black text-xl transition-all duration-300 flex items-center space-x-3 min-w-[280px] justify-center hover:bg-white/20"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Grow Your Community</span>
              <SafeIcon icon={FiArrowRight} />
            </motion.button>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="group"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <div className="bg-black/60 backdrop-blur-xl border border-white/20 group-hover:border-purple-500/50 rounded-3xl p-6 transition-all duration-300">
                  <SafeIcon icon={stat.icon} className="text-3xl text-purple-400 mx-auto mb-3" />
                  <div className="text-3xl lg:text-4xl font-black bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                    {stat.number}
                  </div>
                  <div className="text-xs text-gray-300 font-black uppercase tracking-widest leading-tight">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.2 }}
      >
        <motion.div
          className="w-8 h-12 border-2 border-white/40 rounded-full flex justify-center p-1"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-1.5 h-3 bg-gradient-to-b from-purple-400 to-pink-400 rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;